import React, { useEffect, useState } from "react";
import { Box, TextField, Button, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Sidebar from "./sidebar";
import ProjectFilter from "./ProjectFilter";
import CustomToolbar from "./CustomToolbarPrint";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import {
  inDateRange,
  matchesProject,
  genderProject,
  parseDate,
} from "../utils/reportUtils";

const formatDate = (value) => {
  if (!value) return "";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;
  const d = parseDate(value);
  if (!d) return value;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${d.getFullYear()}`;
};

const summaryColumns = [
  { field: "total", headerName: "Total Attendances", width: 200 },
  { field: "unique", headerName: "Unique Attendees", width: 190 },
  { field: "days", headerName: "Register Days", width: 170 },
  { field: "average", headerName: "Average per Day", width: 170 },
];

const attendanceColumns = [
  { field: "firstName", headerName: "Name", width: 160 },
  { field: "surName", headerName: "Surname", width: 160 },
  { field: "project", headerName: "Project", width: 120 },
  { field: "occasions", headerName: "Occasions", width: 130, type: "number" },
  {
    field: "dates",
    headerName: "Dates Attended",
    width: 480,
    renderCell: (params) => (
      <div style={{ whiteSpace: "normal", lineHeight: 1.4 }}>
        {params.value}
      </div>
    ),
  },
];

export default function DailyRegisterReport() {
  const [date1, setdate1] = useState("");
  const [date2, setdate2] = useState("");
  const [projectFilter, setprojectFilter] = useState([]);
  const [registers, setregisters] = useState([]);
  const [people, setpeople] = useState({});
  const [rows, setRows] = useState([]);
  const [rows1, setRows1] = useState([]);

  useEffect(() => {
    const load = async () => {
      const [employeeSnap, archivedSnap, registerSnap] = await Promise.all([
        getDocs(collection(db, "employee")),
        getDocs(collection(db, "archived")),
        getDocs(collection(db, "dailyRegister")),
      ]);

      const peopleMap = {};
      employeeSnap.forEach((document) => {
        peopleMap[document.id] = { id: document.id, ...document.data() };
      });
      archivedSnap.forEach((document) => {
        const data = document.data();
        const id = data.doc || document.id;
        if (id && !peopleMap[id]) {
          peopleMap[id] = { id, ...data };
        }
      });

      const list = [];
      registerSnap.forEach((document) => {
        const data = document.data();
        list.push({
          id: document.id,
          date: data.date || document.id,
          attendees: Array.isArray(data.attendees) ? data.attendees : [],
        });
      });

      setpeople(peopleMap);
      setregisters(list);
    };
    load();
  }, []);

  const resolvePerson = (attendee) => {
    const match = people[attendee.id] || {};
    return {
      id: attendee.id || match.id || "",
      firstName: attendee.firstName || match.firstName || "",
      surName: attendee.surName || match.surName || "",
      gender: attendee.gender || match.gender || "",
    };
  };

  const handleProjects = (projects) => {
    setprojectFilter(projects);
    setRows([]);
    setRows1([]);
  };

  const getData = () => {
    if (projectFilter.length === 0) {
      setRows([]);
      setRows1([]);
      return;
    }

    const perPerson = new Map();
    let totalAttendances = 0;
    let registerDays = 0;

    registers.forEach((register) => {
      if (!inDateRange(register.date, date1, date2)) return;

      const seen = new Set();
      let dayCount = 0;

      register.attendees.forEach((attendee) => {
        const person = resolvePerson(attendee);
        const personId =
          person.id ||
          `${person.firstName} ${person.surName}`.trim();
        if (!personId) return;
        if (!matchesProject(person.gender, projectFilter)) return;
        if (seen.has(personId)) return;
        seen.add(personId);

        const existing = perPerson.get(personId) || {
          ...person,
          id: personId,
          occasions: 0,
          dates: [],
        };
        existing.occasions += 1;
        existing.dates.push(register.date);
        perPerson.set(personId, existing);
        dayCount += 1;
      });

      if (dayCount > 0) {
        totalAttendances += dayCount;
        registerDays += 1;
      }
    });

    const details = Array.from(perPerson.values())
      .map((person) => ({
        id: person.id,
        firstName: person.firstName,
        surName: person.surName,
        project: genderProject(person.gender),
        occasions: person.occasions,
        dates: Array.from(new Set(person.dates))
          .sort()
          .map(formatDate)
          .join(", "),
      }))
      .sort(
        (a, b) =>
          b.occasions - a.occasions ||
          a.surName.localeCompare(b.surName) ||
          a.firstName.localeCompare(b.firstName)
      );

    setRows(details);
    setRows1([
      {
        id: "summary",
        total: totalAttendances,
        unique: details.length,
        days: registerDays,
        average:
          registerDays > 0
            ? (totalAttendances / registerDays).toFixed(1)
            : "0",
      },
    ]);
  };

  return (
    <Box sx={{ display: "flex" }}>
      <Sidebar />
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3 }}
        style={{ marginTop: "50px" }}
      >
        <div style={{ width: "100%" }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Daily Register Report
          </Typography>

          <ProjectFilter value={projectFilter} onChange={handleProjects} />
          <TextField
            type="date"
            InputLabelProps={{ shrink: true }}
            className="addform"
            label="Start Date"
            variant="outlined"
            style={{ width: "45%" }}
            onChange={(e) => setdate1(e.target.value)}
            value={date1}
          />
          <TextField
            type="date"
            InputLabelProps={{ shrink: true }}
            className="addform"
            label="End Date"
            variant="outlined"
            style={{ width: "45%" }}
            onChange={(e) => setdate2(e.target.value)}
            value={date2}
          />

          <Button
            onClick={getData}
            disabled={projectFilter.length === 0}
            className="addform"
            variant="contained"
            style={{
              marginBottom: "10px",
              width: "100%",
              height: "50px",
              marginTop: "30px",
              backgroundColor: "#F21D2F",
            }}
          >
            Search
          </Button>

          <Typography
            variant="body2"
            sx={{ mb: 2, color: "text.secondary" }}
          >
            Leave both dates blank to include every register.
          </Typography>

          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Overall Summary
          </Typography>
          <div style={{ height: 200, marginBottom: "20px" }}>
            <DataGrid
              rows={rows1}
              columns={summaryColumns}
              pageSize={10}
              rowsPerPageOptions={[10]}
              hideFooter
              components={{ Toolbar: CustomToolbar }}
            />
          </div>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Breakdown
          </Typography>
          <div style={{ height: 520 }}>
            <DataGrid
              rows={rows}
              columns={attendanceColumns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              getRowHeight={() => "auto"}
              components={{ Toolbar: CustomToolbar }}
            />
          </div>
        </div>
      </Box>
    </Box>
  );
}
